import { Injectable, OnDestroy } from '@angular/core';

import { SessionSnapshot } from './memory-oauth-storage';

const CHANNEL_NAME = 'ascent.session';

/** How long a starting tab waits for an answer before it redirects to Cardinal. */
export const ANSWER_TIMEOUT_MS = 150;

type Message =
  | { kind: 'request' }
  | { kind: 'offer', session: SessionSnapshot }
  | { kind: 'signed-out' };

/**
 * Shares the session between tabs of the same origin.
 *
 * This is what keeps "open in a new tab" and a plain reload free of a visible redirect. Tokens live
 * in memory (see MemoryOAuthStorage), so a starting tab has nothing -- it asks the others first, an
 * open tab answers within milliseconds, and only when nobody answers does the redirect happen.
 *
 * It also removes the reason people put refresh tokens into a shared storage. Cardinal rotates
 * refresh tokens, so two tabs reading one storage is a race: one rotates, the other still holds the
 * spent token. Here each tab holds its own copy and they hand it over explicitly.
 *
 * `BroadcastChannel` is missing in old browsers and in some test environments. Then every tab
 * redirects for itself, which is correct but less pleasant -- so this degrades rather than breaks.
 */
@Injectable({
  providedIn: 'root'
})
export class TabSessionChannel implements OnDestroy {
  private readonly _channel: BroadcastChannel | null = createChannel();
  private _offerSession: () => SessionSnapshot | null = () => null;
  private _onSignedOut: () => void = () => { };

  /**
   * Answers the other tabs from here on.
   *
   * A callback rather than an injected store, because the only thing that knows whether a session is
   * still usable is the service that owns it -- and injecting that here would be a cycle.
   */
  public listen(offerSession: () => SessionSnapshot | null, onSignedOut: () => void): void {
    this._offerSession = offerSession;
    this._onSignedOut = onSignedOut;

    if (!this._channel) {
      return;
    }

    this._channel.onmessage = (event: MessageEvent<Message>) => {
      const message = event.data;

      if (message.kind === 'request') {
        const session = this._offerSession();

        // Answer only with a session that is actually usable -- handing over an expired one would
        // make the asking tab believe it is signed in.
        if (session) {
          this.post({ kind: 'offer', session });
        }

        return;
      }

      if (message.kind === 'signed-out') {
        this._onSignedOut();
      }
    };
  }

  /**
   * Asks the other tabs for a session. Resolves with the first answer, or with null after the
   * timeout -- which is the signal to redirect.
   *
   * The timeout is short on purpose: it delays the start of every first tab, where nobody will
   * answer. 150 ms is beyond a same-origin channel round trip and below what a person notices.
   */
  public requestSession(timeoutMs: number = ANSWER_TIMEOUT_MS): Promise<SessionSnapshot | null> {
    const channel = this._channel;

    if (!channel) {
      return Promise.resolve(null);
    }

    return new Promise(resolve => {
      let settled = false;

      const onMessage = (event: MessageEvent<Message>) => {
        if (event.data.kind !== 'offer' || settled) {
          return;
        }

        settled = true;
        channel.removeEventListener('message', onMessage);
        clearTimeout(timer);
        resolve(event.data.session);
      };

      const timer = setTimeout(() => {
        if (settled) {
          return;
        }

        settled = true;
        channel.removeEventListener('message', onMessage);
        resolve(null);
      }, timeoutMs);

      channel.addEventListener('message', onMessage);
      this.post({ kind: 'request' });
    });
  }

  /** Hands a renewed session to the other tabs, so a rotated refresh token reaches them. */
  public publish(session: SessionSnapshot): void {
    this.post({ kind: 'offer', session });
  }

  /** Signing out in one tab signs out in all of them. */
  public publishSignOut(): void {
    this.post({ kind: 'signed-out' });
  }

  public ngOnDestroy(): void {
    this._channel?.close();
  }

  private post(message: Message): void {
    this._channel?.postMessage(message);
  }
}

function createChannel(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'function' ? new BroadcastChannel(CHANNEL_NAME) : null;
}
